import type { AxiosError } from 'axios';
import { Divider, Stack, Text, Title } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { useListCountries, useListCurrencies } from '@/api/generated/reference-data/reference-data';
import { useRegister } from '@/api/generated/registration/registration';
import { IconFigmaArrowLeft, IconFigmaArrowRight } from '@/assets/icons';
import {
  FormErrorMessage,
  MutationButton,
  PasswordField,
  SecondaryEntryCard,
  SelectField,
  TextField
} from '@/components/common';
import { AuthCard } from '@/features/auth/components/AuthCard';
import { brandTextStyle } from '@/features/auth/components/authFieldStyles';
import { EmailAlreadyRegisteredCard } from '@/features/auth/components/EmailAlreadyRegisteredCard';
import { isValidTaxId } from '@/features/auth/components/taxIdFormats';
import { authErrorMessage } from '@/features/auth/utils/authErrorMessage';
import { buildCountryOptions } from '@/utils/format';

// Backend već vraća specifičan, lokalizirani razlog (nevažeća država/valuta/OIB,
// email već registriran...) preveden po Accept-Language headeru — prikazujemo
// ga izravno umjesto generičke poruke, koja bi sakrila zašto je zahtjev odbijen.
// Kod timeouta/network greške nema HTTP odgovora uopće, pa se to provjerava prvo.
const registrationErrorMessage = (
  error: unknown,
  isPaused: boolean,
  t: (key: string) => string,
  fallback: string
): string =>
  authErrorMessage(error, isPaused, t, () => {
    const backendMessage = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
    return typeof backendMessage === 'string' ? backendMessage : fallback;
  });

const schema = z
  .object({
    organisationName: z.string().min(1),
    taxId: z.string().min(1),
    country: z.string().min(1),
    currency: z.string().min(1),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    email: z.string().email(),
    password: z.string().min(8),
    confirmPassword: z.string().min(8)
  })
  .refine((values) => isValidTaxId(values.country, values.taxId), {
    message: 'invalidTaxId',
    path: ['taxId']
  })
  .refine((values) => values.password === values.confirmPassword, {
    message: 'passwordsMustMatch',
    path: ['confirmPassword']
  });

type FormValues = z.infer<typeof schema>;

interface RegisterFormProps {
  onEmailConflictChange?: (emailConflict: boolean) => void;
}

export const RegisterForm = ({ onEmailConflictChange }: RegisterFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: countries, isLoading: countriesLoading } = useListCountries();
  const { data: currencies, isLoading: currenciesLoading } = useListCurrencies();
  const countryOptions = buildCountryOptions(countries);
  const currencyOptions =
    currencies?.map((c) => ({ value: c.code ?? '', label: `${c.code ?? ''} — ${c.name ?? ''}` })) ??
    [];

  const form = useForm<FormValues>({
    validate: zodResolver(schema),
    initialValues: {
      organisationName: '',
      taxId: '',
      country: 'HR',
      currency: 'EUR',
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: ''
    }
  });

  const registerMutation = useRegister({
    mutation: {
      onSuccess: (_data, variables) => {
        navigate(`/verify-email?email=${encodeURIComponent(variables.data.email)}`);
      }
    }
  });

  const handleSubmit = (values: FormValues) => {
    registerMutation.mutate({
      data: {
        organisationName: values.organisationName,
        taxId: values.taxId,
        country: values.country,
        currency: values.currency,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password
      }
    });
  };

  const emailAlreadyRegistered =
    registerMutation.isError && (registerMutation.error as AxiosError)?.response?.status === 409;

  useEffect(() => {
    onEmailConflictChange?.(emailAlreadyRegistered);
  }, [emailAlreadyRegistered, onEmailConflictChange]);

  if (emailAlreadyRegistered) {
    return <EmailAlreadyRegisteredCard />;
  }

  return (
    <AuthCard p={{ base: 24, md: 40 }} w={{ base: '100%', md: 520 }}>
      <Stack gap={20}>
        <Stack gap={6}>
          <Title order={2} fz={{ base: 20, md: 24 }} fw={700} style={brandTextStyle}>
            {t('registration.formTitle')}
          </Title>
          <Text fz={{ base: 12.5, md: 13 }} style={{ color: 'var(--color-text-muted)' }}>
            {t('registration.formSubtitle')}
          </Text>
        </Stack>

        {(registerMutation.isError || registerMutation.isPaused) && (
          <FormErrorMessage
            message={registrationErrorMessage(
              registerMutation.error,
              registerMutation.isPaused,
              t,
              t('registration.submitError')
            )}
          />
        )}

        <form onSubmit={form.onSubmit(handleSubmit)} noValidate>
          <Stack gap={12}>
            <TextField
              label={t('registration.companyName')}
              required
              {...form.getInputProps('organisationName')}
            />

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: 12
              }}
            >
              <TextField
                label={t('registration.taxId')}
                placeholder={t(
                  form.values.country === 'DE'
                    ? 'registration.taxIdPlaceholderDe'
                    : 'registration.taxIdPlaceholder'
                )}
                required
                {...form.getInputProps('taxId')}
                error={form.errors.taxId ? t('registration.invalidTaxId') : undefined}
              />
              <SelectField
                label={t('registration.country')}
                allowDeselect={false}
                searchable
                disabled={countriesLoading}
                data={countryOptions}
                {...form.getInputProps('country')}
              />
            </div>

            <SelectField
              label={t('registration.currency')}
              allowDeselect={false}
              searchable
              disabled={currenciesLoading}
              data={currencyOptions}
              {...form.getInputProps('currency')}
            />

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: 12
              }}
            >
              <TextField
                label={t('registration.firstName')}
                required
                {...form.getInputProps('firstName')}
              />
              <TextField
                label={t('registration.lastName')}
                required
                {...form.getInputProps('lastName')}
              />
            </div>

            <TextField label={t('registration.email')} required {...form.getInputProps('email')} />

            <PasswordField
              label={t('registration.password')}
              required
              {...form.getInputProps('password')}
            />

            <PasswordField
              label={t('registration.confirmPassword')}
              required
              {...form.getInputProps('confirmPassword')}
              error={form.errors.confirmPassword ? t('registration.passwordsMustMatch') : undefined}
            />

            <MutationButton
              type="submit"
              fullWidth
              rightSection={<IconFigmaArrowRight size={15} />}
              isPending={registerMutation.isPending}
              isPaused={registerMutation.isPaused}
            >
              {t('registration.submit')}
            </MutationButton>
          </Stack>
        </form>

        <Divider
          label={t('auth.or')}
          labelPosition="center"
          color="var(--color-bg-surface-3)"
          styles={{ label: { color: 'var(--color-text-secondary)', fontSize: 12 } }}
        />

        <SecondaryEntryCard
          icon={IconFigmaArrowLeft}
          title={t('auth.backToLoginTitle')}
          subtitle={t('auth.backToLoginSubtitle')}
          to="/login"
          showTrailingArrow={false}
        />
      </Stack>
    </AuthCard>
  );
};
